/**
 * 考試規劃 — per-profile 儲存。
 * Source of truth: docs/spec/EXAM_PLANNER_SPEC.md §2。
 *
 * key `eternal-fortress.exam-plan.<profileId>`(沿用 profile 慣例,不同小孩各自一份)。
 * 純學習側,不碰任何戰鬥數值;任何錯誤吞掉降級成空表(絕不阻斷)。
 */
import { getCurrentProfileId } from '../../shared/profile';
import type { ExamEvent, ExamType, Subject } from './types';

const keyFor = (pid: string): string => `eternal-fortress.exam-plan.${pid}`;

const VALID_TYPES: ReadonlySet<string> = new Set<ExamType>(['midterm', 'final', 'quiz']);
const VALID_SUBJECTS: ReadonlySet<string> = new Set<Subject>(['math', 'chinese', 'english', 'nature', 'social']);
function isExamEvent(x: unknown): x is ExamEvent {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  // subject 可不存在;存在時必須是合法 Subject。
  if (o.subject !== undefined && !(typeof o.subject === 'string' && VALID_SUBJECTS.has(o.subject))) return false;
  return (
    typeof o.id === 'string' &&
    typeof o.name === 'string' &&
    typeof o.type === 'string' &&
    VALID_TYPES.has(o.type) &&
    typeof o.date === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(o.date)
  );
}

/** 讀目前 profile 的考試規劃(壞資料過濾掉;讀不到回空陣列)。 */
export function loadExamPlan(): ExamEvent[] {
  try {
    const raw = localStorage.getItem(keyFor(getCurrentProfileId()));
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? arr.filter(isExamEvent) : [];
  } catch {
    return [];
  }
}

/** 覆寫目前 profile 的考試規劃。 */
export function saveExamPlan(events: ExamEvent[]): void {
  try {
    localStorage.setItem(keyFor(getCurrentProfileId()), JSON.stringify(events));
  } catch {
    /* 寫入失敗不阻斷 */
  }
}

/** 產生穩定 id(瀏覽器端;測試可注入)。 */
function genId(): string {
  return `ex_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** 新增一筆考試,回傳新事件(已存檔)。小考帶 subject;段考省略(全科)。 */
export function addExamEvent(input: { name?: string; type: ExamType; date: string; subject?: Subject }): ExamEvent {
  const event: ExamEvent = {
    id: genId(),
    name: input.name?.trim() ?? '',
    type: input.type,
    date: input.date,
    ...(input.subject ? { subject: input.subject } : {}),
  };
  const events = loadExamPlan();
  events.push(event);
  saveExamPlan(events);
  return event;
}

/** 刪一筆。 */
export function removeExamEvent(id: string): void {
  saveExamPlan(loadExamPlan().filter((e) => e.id !== id));
}

/** 改一筆(name/type/date/subject)。找不到 id 則 no-op。 */
export function updateExamEvent(id: string, patch: Partial<Pick<ExamEvent, 'name' | 'type' | 'date' | 'subject'>>): void {
  saveExamPlan(loadExamPlan().map((e) => (e.id === id ? { ...e, ...patch } : e)));
}
